/**
 * Lightweight client-side analytics emitter.
 *
 * Fire-and-forget — never blocks UI, never surfaces failures to the user.
 * Posts to the existing same-origin backend at /api/v1/marketing/events so
 * cookies + CORS are already correct.
 *
 * Three events fire across the upgrade funnel:
 *   • upgrade_prompt_viewed    — card mounts
 *   • upgrade_prompt_clicked   — coach taps the CTA
 *   • upgrade_checkout_started — Stripe checkout opens
 *
 * Every event includes: feature_name, source_screen, current_tier, team_id.
 */
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND}/api/v1/marketing/events`;

// Avoid spamming the network if the same view fires multiple times in quick
// succession (e.g. React StrictMode double-mount in dev). Tiny in-memory dedupe.
const recent = new Map();
const DEDUPE_WINDOW_MS = 1500;

export function track(event, properties = {}) {
  const key = `${event}:${properties.feature_name || ""}:${properties.source_screen || ""}`;
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return;
  recent.set(key, now);

  const payload = {
    event,
    properties: {
      ts: new Date().toISOString(),
      ...properties,
    },
  };
  // Fire-and-forget. axios with `withCredentials` matches the rest of the app.
  axios
    .post(ENDPOINT, payload, { withCredentials: true, timeout: 4000 })
    .catch(() => { /* swallow — analytics must never break UX */ });
}
